import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  EyeIcon, EyeSlashIcon, LockKeyIcon, CheckCircleIcon, XCircleIcon,
} from "@phosphor-icons/react";

/**
 * /reset-password?token=xyz
 * Valida el token (muestra a quién pertenece) y permite fijar nueva contraseña.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenErr, setTokenErr] = useState("");

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setTokenErr("El enlace no incluye un token válido.");
      setChecking(false);
      return;
    }
    api.get(`/auth/reset-token-info?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setTokenInfo(data))
      .catch((e) => setTokenErr(apiErr(e)))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("La contraseña debe tener al menos 8 caracteres."); return; }
    if (pw !== pw2) { setErr("Las contraseñas no coinciden."); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Contraseña actualizada");
      setTimeout(() => navigate("/login"), 1200);
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setSaving(false);
    }
  };

  // Barra de fortaleza básica
  const strength = (() => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (pw.length >= 12) s++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
    if (/[0-9]/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s; // 0..5
  })();
  const strengthLabel = ["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte", "Excelente"][strength];
  const strengthColor = ["bg-red-500", "bg-red-500", "bg-orange-500", "bg-yellow-500", "bg-agro-green", "bg-agro-green"][strength];

  return (
    <div className="min-h-screen flex bg-app items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-xl" data-testid="reset-password-card">
        <div className="flex items-center justify-center mb-6">
          <img src="/agroport-logo-green.png" alt="AGROPORT" className="h-14 w-auto" />
        </div>

        {checking ? (
          <div className="text-center py-8">
            <div className="text-sm text-text-muted">Validando enlace…</div>
          </div>
        ) : tokenErr ? (
          <div className="text-center space-y-4" data-testid="reset-token-error">
            <XCircleIcon size={48} weight="fill" className="text-red-400 mx-auto" />
            <h2 className="font-heading text-2xl font-semibold text-text-primary">Enlace inválido</h2>
            <p className="text-sm text-text-secondary">{tokenErr}</p>
            <div className="pt-2 space-y-2">
              <Link
                to="/forgot-password"
                className="inline-block bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 px-6 text-sm transition"
                data-testid="request-new-link"
              >
                Solicitar un nuevo enlace
              </Link>
              <div>
                <Link to="/login" className="text-xs text-text-muted hover:text-agro-green">
                  Volver a iniciar sesión
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-agro-green font-medium mb-2">Nueva contraseña</div>
              <h2 className="font-heading text-2xl font-semibold text-text-primary">
                Hola, {tokenInfo?.name || tokenInfo?.email}
              </h2>
              <p className="text-sm text-text-secondary mt-2">
                Crea una nueva contraseña para <b className="text-text-primary">{tokenInfo?.email}</b>.
                Todas tus sesiones activas se cerrarán por seguridad.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-text-secondary font-medium mb-2 block">Nueva contraseña</label>
                <div className="relative">
                  <LockKeyIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    data-testid="reset-pw-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    minLength={8}
                    className="w-full bg-app border border-border rounded-md pl-10 pr-10 py-3 text-sm text-text-primary focus:border-agro-green"
                    placeholder="Mín. 8 caracteres"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                    data-testid="reset-toggle-visibility"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />}
                  </button>
                </div>
                {pw && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-border rounded overflow-hidden">
                      <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(strength / 5) * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-text-muted whitespace-nowrap">{strengthLabel}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-text-secondary font-medium mb-2 block">Confirmar contraseña</label>
                <div className="relative">
                  <LockKeyIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    data-testid="reset-pw2-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    required
                    minLength={8}
                    className="w-full bg-app border border-border rounded-md pl-10 pr-3 py-3 text-sm text-text-primary focus:border-agro-green"
                    placeholder="Repite la contraseña"
                  />
                </div>
                {pw2 && pw && (
                  pw === pw2
                    ? <div className="mt-1.5 text-[11px] text-agro-green flex items-center gap-1"><CheckCircleIcon size={12} weight="fill" /> Coinciden</div>
                    : <div className="mt-1.5 text-[11px] text-red-400 flex items-center gap-1"><XCircleIcon size={12} weight="fill" /> No coinciden</div>
                )}
              </div>

              {err && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="reset-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={saving || !pw || !pw2}
                data-testid="reset-submit"
                className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {saving ? "Guardando…" : "Actualizar contraseña"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
